import {CoreBaseEffects} from "@core/core-base.effects";
import {Observable} from "rxjs";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {Store} from "@ngrx/store";
import {Injectable} from "@angular/core";
import {TechInventoryActions} from "@tech/pages/inventory/store/tech-inventory.actions-type";
import {
  AcceptEditorRequestDto,
  DeclineEditorRequestDto,
  LocationSimpleDto,
  MissionDto,
  MissionReportDto,
  MissionState,
  ResourceState
} from "@server-models";
import {ISharedLocalBodyRequest} from "@shared/interfaces/shared-local-body-request.interface";
import {getArticleRestrictions} from "@tech/pages/inventory/store/tech-inventory.actions";

@Injectable({
  providedIn: 'root'
})
export abstract class TechInventoryBaseEffects extends CoreBaseEffects {

  protected constructor(store: Store, rawActions$: Actions) {
    super(store, rawActions$);
  }

  abstract postMissionPaginated(action$: Observable<ISharedLocalBodyRequest>): Observable<any>;

  // detail
  abstract navigateToMissionDetail(action$: Observable<{ missionId: number }>): Observable<any>;

  abstract getMissionDetailById(action$: Observable<{ missionId: string; existingUrl: string[] }>): Observable<any>;

  abstract getMissionDetailByIdSuccess(action$: Observable<{
    data: MissionDto;
    existingUrl: string[]
  }>): Observable<any>;

  abstract setMissionState(action$: Observable<{ id: number; stateToChange: MissionState; reason: string | null }>): Observable<any>;

  abstract setMissionStateSuccess(action$: Observable<any>): Observable<any>;

  // requested

  abstract postMissionRequestedPaginated(action$: Observable<ISharedLocalBodyRequest>): Observable<any>;

  abstract postMissionEditorQuestAccept(action$: Observable<{ missionId: number; body: AcceptEditorRequestDto }>): Observable<any>;

  abstract postMissionEditorQuestDecline(action$: Observable<{ missionId: number; body: DeclineEditorRequestDto }>): Observable<any>;

  abstract postMissionEditorQuestDone(action$: Observable<any>): Observable<any>;

  // report
  abstract getMissionReportById(action$: Observable<{ reportId: string }>): Observable<any>;

  abstract getMissionReportPreviewById(action$: Observable<{ missionId: string }>): Observable<any>;

  abstract getMissionReportArticleList(action$: Observable<any>): Observable<any>;

  abstract submitMissionReport(action$: Observable<{
    missionReport: MissionReportDto,
    hasReportId: boolean
  }>): Observable<any>;

  abstract submitMissionReportSuccess(action$: Observable<{ response: MissionReportDto }>): Observable<any>;

  abstract navigateToInventory(action$: Observable<any>): Observable<any>;

  // resource
  abstract navigateToResourceDetail(action$: Observable<{ resourceId: any }>): Observable<any>;

  abstract getResourceDetailById(action$: Observable<{ resourceId: string; existingUrl: string[] }>): Observable<any>;

  abstract setResourceState(action$: Observable<{ resourceId: number; state: ResourceState }>): Observable<any>;

  abstract setResourceStateSuccess(action$: Observable<{ resourceId: number; state: ResourceState }>): Observable<any>;

  abstract setResourceLocation(action$: Observable<{
    resourceId: number;
    state: ResourceState;
    location: LocationSimpleDto;
  }>): Observable<any>;

  abstract setResourceLocationSuccess(action$: Observable<{
    resourceId: number;
    location: LocationSimpleDto;
  }>): Observable<any>;

  // resource missions
  abstract getResourceDetailByIdMissionsPaginated(action$: Observable<any>): Observable<any>;

  abstract getArticleRestrictions(action$: Observable<{ stereotypeId: number }>): Observable<any>;

  postMissionPaginated$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.postMissionPagePaginated),
    action => this.postMissionPaginated(action)
  ));

  navigateToMissionDetail$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.navigateToMissionDetail),
    action => this.navigateToMissionDetail(action)
  ), { dispatch: false });

  getMissionDetailById$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.getMissionDetailById),
    action => this.getMissionDetailById(action)
  ));

  getMissionDetailByIdSuccess$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.getMissionDetailByIdSuccess),
    action => this.getMissionDetailByIdSuccess(action)
  ));

  setMissionState$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.setMissionState),
    action => this.setMissionState(action)
  ));

  setMissionStateSuccess$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.setMissionStateSuccess),
    action => this.setMissionStateSuccess(action)
  ), { dispatch: false });

  // requested
  postMissionRequestedPaginated$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.postMissionRequestedPagePaginated),
    action => this.postMissionRequestedPaginated(action)
  ));

  postMissionEditorQuestAccept$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.postMissionEditorQuestAccept),
    action => this.postMissionEditorQuestAccept(action)
  ));

  postMissionEditorQuestDecline$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.postMissionEditorQuestDecline),
    action => this.postMissionEditorQuestDecline(action)
  ));

  postMissionEditorQuestDone$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.postMissionEditorQuestDone),
    action => this.postMissionEditorQuestDone(action)
  ));

  // report
  getMissionReportById$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.getMissionReportById),
    action => this.getMissionReportById(action)
  ));

  getMissionReportPreviewById$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.getMissionReportPreviewById),
    action => this.getMissionReportPreviewById(action)
  ));

  getMissionReportArticleList$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.getMissionReportArticleList),
    action => this.getMissionReportArticleList(action)
  ));

  submitMissionReport$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.submitMissionReport),
    action => this.submitMissionReport(action)
  ));

  submitMissionReportSuccess$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.submitMissionReportSuccess),
    action => this.submitMissionReportSuccess(action)
  ), { dispatch: false });

  navigateToInventory$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.navigateToInventory),
    action => this.navigateToInventory(action)
  ), { dispatch: false });

  // resource
  navigateToResourceDetail$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.navigateToResourceDetail),
    action => this.navigateToResourceDetail(action)
  ), { dispatch: false });

  getResourceDetailById$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.getResourceDetailById),
    action => this.getResourceDetailById(action)
  ));

  setResourceState$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.setResourceState),
    action => this.setResourceState(action)
  ));

  setResourceStateSuccess$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.setResourceStateSuccess),
    action => this.setResourceStateSuccess(action)
  ), { dispatch: false });

  setResourceLocation$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.setResourceLocation),
    action => this.setResourceLocation(action)
  ));

  setResourceLocationSuccess$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.setResourceLocationSuccess),
    action => this.setResourceLocationSuccess(action)
  ), { dispatch: false });

  // resource missions
  getResourceDetailByIdMissionsPaginated$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.getResourceDetailByIdMissionsPaginated),
    action => this.getResourceDetailByIdMissionsPaginated(action)
  ));

  getArticleRestrictions$ = createEffect(() => this.actions().pipe(
    ofType(TechInventoryActions.getArticleRestrictions),
    action => this.getArticleRestrictions(action)
  ));
}
